import { useRef, useState, useCallback } from "react";
import { api, errMsg } from "@/lib/api";

const pickMime = () => {
  if (typeof MediaRecorder === "undefined") return null;
  return ["audio/webm;codecs=opus", "audio/webm", "audio/mp4", "audio/ogg"].find((m) => MediaRecorder.isTypeSupported(m)) || "";
};

export function useVoice({ onTranscript }) {
  const [state, setState] = useState("idle");
  const [error, setError] = useState(null);
  const recRef = useRef(null);
  const audioRef = useRef(null);

  const stopSpeaking = useCallback(() => {
    if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; }
    setState((s) => (s === "speaking" ? "idle" : s));
  }, []);

  const start = async () => {
    stopSpeaking();
    setError(null);
    const mime = pickMime();
    if (!navigator.mediaDevices?.getUserMedia || mime === null) {
      setError("This browser can't capture audio. You can keep typing instead.");
      setState("error");
      return;
    }
    setState("requesting");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const rec = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      const chunks = [];
      rec.ondataavailable = (e) => e.data.size && chunks.push(e.data);
      rec.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const type = (rec.mimeType || "audio/webm").split(";")[0];
        const blob = new Blob(chunks, { type });
        if (blob.size < 800) { setState("idle"); setError("That was too short to hear. Hold on a little longer."); return; }
        setState("transcribing");
        const form = new FormData();
        form.append("file", blob, `speech.${type.split("/")[1]}`);
        try {
          const { data } = await api.post("/voice/transcribe", form);
          setState("idle");
          onTranscript(data.text);
        } catch (e) { setError(errMsg(e)); setState("error"); }
      };
      recRef.current = rec;
      rec.start();
      setState("listening");
    } catch (e) {
      setError(e?.name === "NotAllowedError" ? "Microphone permission was denied. Allow it in your browser, or keep typing." : "Could not access a microphone.");
      setState("error");
    }
  };

  const stop = () => { if (recRef.current?.state === "recording") recRef.current.stop(); };
  const toggle = () => (state === "listening" ? stop() : start());

  const speak = async (text) => {
    if (!text) return;
    try {
      setState("speaking");
      const res = await api.post("/voice/speak", { text: text.slice(0, 4000) }, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = new Audio(url);
      audioRef.current = a;
      a.onended = () => { URL.revokeObjectURL(url); audioRef.current = null; setState("idle"); };
      await a.play();
    } catch (e) {
      setState("idle");
      setError(`Spoken reply unavailable: ${errMsg(e)}`);
    }
  };

  return { state, error, setError, toggle, start, stop, speak, stopSpeaking };
}
